import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import { LogoMark } from "@/components/Logo";
import { ArrowRight, Loader2, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_TEXTURE =
  "https://static.prod-images.emergentagent.com/jobs/d6f58c33-0a17-4479-ae12-e5dc7e28c8d7/images/4c6f40be4feba300b897dc953ac5cc34259aecb0447891e3d4fc0ecda8ec70c8.png";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Newsletter() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [reward, setReward] = useState(null); // { code, percent }
  const [copied, setCopied] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      toast.error(t.newsletter.invalid);
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email: trimmed, language: lang });
      toast.success(t.newsletter.success);
      setEmail("");
      if (res.data?.discount_code) {
        setReward({
          code: res.data.discount_code,
          percent: res.data.discount_percent || 15,
        });
      } else {
        setReward({ code: null, percent: null });
      }
    } catch (err) {
      toast.error(t.newsletter.error);
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!reward?.code) return;
    try {
      await navigator.clipboard.writeText(reward.code);
      setCopied(true);
      toast.success(t.newsletter.codeCopied);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t.newsletter.codeCopyFail);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-28 sm:py-36 bg-[#050505] overflow-hidden kh-section-cinematic"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at center, rgba(123, 30, 46, 0.32), transparent 65%)" }}
      />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 md:px-12">
        <div
          className="relative border border-[#D4AF37]/30 overflow-hidden kh-belt-frame kh-noise"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 5, 5, 0.92), rgba(74, 15, 28, 0.78)), url(${BRAND_TEXTURE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="relative px-6 sm:px-12 md:px-20 py-20 sm:py-28 text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#A93043]" />
              <span className="kh-chapter">{t.newsletter.chapter}</span>
              <span className="h-px w-8 bg-[#A93043]" />
            </div>

            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 blur-2xl opacity-60"
                style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }}
              />
              <div className="relative">
                <LogoMark size={56} />
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="kh-eyebrow">{t.newsletter.eyebrow}</span>
              <span className="h-px w-12 bg-[#D4AF37]" />
            </div>

            <h2
              data-testid="newsletter-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-white leading-[0.95]"
            >
              {t.newsletter.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="font-serif-luxury italic text-[#D4AF37] normal-case tracking-normal">
                {t.newsletter.title.split(" ").slice(-1)[0]}
              </span>
            </h2>
            <p className="mt-6 font-body text-base sm:text-lg text-white/65 max-w-xl mx-auto">
              {t.newsletter.subtitle}
            </p>

            {!reward && (
              <form
                onSubmit={onSubmit}
                data-testid="newsletter-form"
                className="mt-10 flex flex-col sm:flex-row max-w-xl mx-auto"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.newsletter.placeholder}
                  data-testid="newsletter-email-input"
                  disabled={busy}
                  className="flex-1 bg-black/60 backdrop-blur border border-[#1f1f29] focus:border-[#D4AF37] focus:outline-none text-white font-body text-base px-6 py-4 placeholder:text-white/35 disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit"
                  className="bg-[#D4AF37] hover:bg-[#F0D481] text-black font-display text-lg tracking-[0.15em] uppercase px-8 py-4 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {busy ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t.newsletter.submit}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Success reward card */}
            {reward && (
              <div
                data-testid="newsletter-reward"
                className="mt-10 max-w-xl mx-auto kh-fade-up"
              >
                <p
                  data-testid="newsletter-success"
                  className="font-serif-luxury italic text-base text-[#D4AF37] mb-6"
                >
                  "{t.newsletter.success}"
                </p>

                {reward.code ? (
                  <div className="relative border border-[#D4AF37] bg-black/55 backdrop-blur p-6 sm:p-7">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 bg-[#0a0606] font-body text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">
                      {t.newsletter.rewardLabel}
                    </div>

                    <p className="font-body text-xs sm:text-sm text-white/65 mb-4">
                      {(t.newsletter.rewardIntro || "").replace("{p}", String(reward.percent))}
                    </p>

                    <button
                      type="button"
                      onClick={copyCode}
                      data-testid="newsletter-copy-code"
                      className="group w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 bg-[#D4AF37] text-black hover:bg-[#F0D481] transition-colors"
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-body text-[10px] tracking-[0.3em] uppercase opacity-70">
                          {t.newsletter.codeLabel}
                        </span>
                        <span
                          data-testid="newsletter-code"
                          className="font-display text-2xl sm:text-3xl tracking-[0.18em]"
                          style={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {reward.code}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 font-body text-[11px] tracking-[0.25em] uppercase">
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            {t.newsletter.copied}
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            {t.newsletter.copy}
                          </>
                        )}
                      </div>
                    </button>

                    <p className="mt-4 font-body text-[11px] text-white/45 leading-relaxed">
                      {t.newsletter.rewardHint}
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
